import React from 'react'
import type { PresentationData } from '../../types/presentation'
import {
  EnhancedTitleSlide,
  EnhancedGridSlide,
  EnhancedContentSlide,
  FeatureCard,
  InfoBox,
} from '../../components/presentation'
import { IconSolid, PromptModal } from '../../components/presentation'
import contextEngineeringPrompts from './contextEngineeringPrompts'

/**
 * Context Engineering - Professionelle Grundlagen (Deutsch)
 * 
 * Systematische KI-Entwicklungsgrundlagen für Enterprise-Umgebungen
 */

export const contextEngineeringProfessionalDe: PresentationData = {
  metadata: {
    title: 'Context Engineering - Professionelle Grundlagen',
    description: 'Systematische Methodik für effiziente KI-Entwicklung',
    author: 'Jan Pfrommer',
    date: 'Oktober 2025',
    tags: ['Context Engineering', 'KI-Entwicklung', 'Enterprise', 'Systematisch'],
  },
  slides: [
    // ============================================
    // SLIDE 1: Title
    // ============================================
    {
      id: 1,
      component: () => (
        <EnhancedTitleSlide
          title="CONTEXT ENGINEERING"
          subtitle="Professionelle Grundlagen für effiziente KI-Entwicklung"
          author="Jan Pfrommer"
          date="Oktober 2025"
          tags={['🎯 Systematischer Ansatz', '📈 Messbare Ergebnisse', '⚡ Produktionstauglich']}
          background="primary"
          footer={
            <div className="text-center text-lg font-light text-white/90">
              Von einfachen Prompts zu Enterprise AI-Workflows
            </div>
          }
        />
      ),
      transition: 'fade',
    },

    // ============================================
    // SLIDE 2: About Me
    // ============================================
    {
      id: 2,
      component: () => (
        <EnhancedGridSlide
          title="Über mich"
          subtitle="Expertise in Context Engineering & Enterprise AI"
          columns={2}
          background="secondary"
          items={[
            <div className="flex flex-col items-center text-center">
              <div className="w-48 h-48 rounded-full overflow-hidden bg-gradient-to-br from-primary-600 to-secondary-600 mb-6 flex items-center justify-center shadow-2xl">
                <img src="/src/assets/jan.png" alt="Jan Pfrommer" className="w-full h-full object-cover" />
              </div>
              <h3 className="text-2xl font-bold mb-4 text-primary-600 dark:text-primary-400">Jan Pfrommer</h3>
              <div className="text-lg space-y-2 text-gray-700 dark:text-gray-300">
                <p>• Software-Ingenieur</p>
                <p>• Context Engineering Spezialist</p>
                <p>• Enterprise AI-Lösungsexperte</p>
              </div>
            </div>,
            <div className="space-y-6">
              <FeatureCard
                icon={<IconSolid name="graduation-cap" size="xl" className="text-primary-600" />}
                title="6+ Jahre Software-Engineering"
                description="Umfassende Erfahrung in Enterprise-Entwicklung"
              />
              <FeatureCard
                icon={<IconSolid name="brain" size="xl" className="text-secondary-600" />}
                title="KI & Context Engineering"
                description="Spezialisiert auf systematische AI-Integration"
              />
              <FeatureCard
                icon={<IconSolid name="building" size="xl" className="text-accent-600" />}
                title="Enterprise Solutions"
                description="Produktionstaugliche KI-Systeme"
              />
            </div>,
          ]}
        />
      ),
      transition: 'slide',
    },

    // ============================================
    // SLIDE 3: Was ist Context Engineering?
    // ============================================
    {
      id: 3,
      component: () => (
        <EnhancedContentSlide
          title="Was ist Context Engineering?"
          subtitle="Die systematische Methodik für optimale KI-Interaktion"
          background="light"
        >
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <FeatureCard
              icon={<IconSolid name="network-wired" size="lg" className="text-primary-600" />}
              title="Strukturierte Kommunikation"
              description="mit KI-Systemen"
              items={[
                'Präzise Aufgabendefinition',
                'Kontextuelle Informationen',
                'Erwartungsmanagement'
              ]}
            />
            <FeatureCard
              icon={<IconSolid name="sync-alt" size="lg" className="text-secondary-600" />}
              title="Reproduzierbare Arbeitsabläufe"
              description="für konsistente Ergebnisse"
              items={[
                'Wiederholbare Prozesse', 
                'Standardisierte Vorgehensweisen',
                'Qualitätssicherung'
              ]}
            />
            <FeatureCard
              icon={<IconSolid name="project-diagram" size="lg" className="text-accent-600" />}
              title="Kontext-Management"
              description="für komplexe Projekte"
              items={[
                'Informationsarchitektur',
                'Abhängigkeiten verwalten',
                'Skalierbare Strukturen'
              ]}
            />
            <FeatureCard
              icon={<IconSolid name="shield-alt" size="lg" className="text-green-600" />}
              title="Enterprise-Integration"
              description="in bestehende Systeme"
              items={[
                'API-Integration',
                'Sicherheitsstandards',
                'Compliance-Anforderungen'
              ]}
            />
          </div>
        </EnhancedContentSlide>
      ),
      transition: 'slide',
    },

    // ============================================
    // SLIDE 4: Business Impact
    // ============================================
    {
      id: 4,
      component: () => (
        <EnhancedGridSlide
          title="Business Impact"
          subtitle="Warum Context Engineering essentiell ist"
          columns={2}
          background="primary"
          items={[
            <FeatureCard
              icon={<IconSolid name="times-circle" size="2xl" className="text-red-400" />}
              title="❌ Ad-hoc KI-Nutzung"
              description="Unvorhersagbare, kostspielige Ergebnisse"
              items={[
                'Unvorhersagbare Ergebnisse',
                'Hoher Zeitaufwand für Korrekturen',
                'Inkonsistente Code-Qualität',
                'Team-Frustration',
                'Schwer skalierbare Prozesse',
                'Keine Qualitätskontrolle'
              ]}
              variant="glass"
            />,
            <FeatureCard
              icon={<IconSolid name="check-circle" size="2xl" className="text-green-400" />}
              title="✅ Systematisches Context Engineering"
              description="Vorhersagbar, effizient, skalierbar"
              items={[
                'Konsistente, hochwertige Outputs',
                'Erste Iteration oft bereits optimal',
                'Enterprise-Grade Standards',
                'Effizienter, vorhersagbarer Workflow',
                'Team-kompatible Methoden',
                'Messbare Qualitätsverbesserung'
              ]}
              variant="glass"
            />,
          ]}
          footer={
            <InfoBox variant="success">
              <strong>ROI:</strong> Unternehmen berichten von 3-5x Produktivitätssteigerung durch systematisches Context Engineering
            </InfoBox>
          }
        />
      ),
      transition: 'slide',
    },

    // ============================================
    // SLIDE 5: Die 5 Säulen
    // ============================================
    {
      id: 5,
      component: () => (
        <EnhancedGridSlide
          title="Die 5 Säulen des Context Engineering"
          subtitle="Fundamentale Prinzipien für systematischen Erfolg"
          columns={3}
          background="secondary"
          items={[
            <FeatureCard
              icon={<IconSolid name="bullseye" size="2xl" className="text-primary-600" />}
              title="1. Klarheit"
              description="Präzise Zieldefinition"
              items={[
                'Eindeutige Aufgabenstellung',
                'Messbarer Erfolg',
                'Definierter Umfang'
              ]}
              variant="elevated"
            />,
            <FeatureCard
              icon={<IconSolid name="sitemap" size="2xl" className="text-secondary-600" />}
              title="2. Struktur"
              description="Systematischer Aufbau"
              items={[
                'Logische Informationshierarchie',
                'Standardisierte Formate',
                'Wiederverwendbare Patterns'
              ]}
              variant="elevated"
            />,
            <FeatureCard
              icon={<IconSolid name="database" size="2xl" className="text-accent-600" />}
              title="3. Kontext"
              description="Relevantes Informationsmanagement"
              items={[
                'Technische Rahmenbedingungen',
                'Business-Anforderungen',
                'Historischer Kontext'
              ]}
              variant="elevated"
            />,
            <FeatureCard
              icon={<IconSolid name="sync-alt" size="2xl" className="text-green-600" />}
              title="4. Iteration"
              description="Kontinuierliche Optimierung"
              items={[
                'Feedback-Schleifen',
                'Inkrementelle Verbesserungen',
                'Lernen aus Ergebnissen'
              ]}
              variant="elevated"
            />,
            <FeatureCard
              icon={<IconSolid name="chart-line" size="2xl" className="text-blue-600" />}
              title="5. Messung"
              description="Quantifizierbare Erfolgskontrolle"
              items={[
                'KPIs definieren',
                'Qualitätsmetriken',
                'Performance-Tracking'
              ]}
              variant="elevated"
            />,
          ]}
        />
      ),
      transition: 'slide',
    },

    // ============================================
    // SLIDE 6: Enterprise Prompt Framework
    // ============================================
    {
      id: 6,
      component: () => (
        <EnhancedContentSlide
          title="Enterprise Prompt Framework"
          subtitle="Strukturierter Ansatz für professionelle KI-Kommunikation"
          background="light"
        >
          <div className="bg-slate-900 text-cyan-400 p-6 rounded-lg font-mono text-sm mb-6 overflow-x-auto">
            <div className="space-y-4">
              <div>
                <span className="text-green-400"># ROLLENDEFINITION</span><br/>
                <span className="text-white">Sie sind ein Senior Software-Architekt mit 15+ Jahren Enterprise-Erfahrung<br/>
                Spezialisierung: Microservices, Cloud-Architektur, Sicherheits-Best-Practices</span>
              </div>
              <div>
                <span className="text-green-400"># GESCHÄFTSKONTEXT</span><br/>
                <span className="text-white">Projekt: Migration einer Finanzdienstleistungsplattform<br/>
                Aktuell: Legacy Monolith (Java/Spring) → Cloud-Native Architektur<br/>
                Einschränkungen: DSGVO-Konformität, 99.9% SLA, PCI-DSS Level 1</span>
              </div>
              <div>
                <span className="text-green-400"># TECHNISCHE ANFORDERUNGEN</span><br/>
                <span className="text-white">Stack: Kubernetes, Service Mesh, Event-Driven Architektur<br/>
                Datenbank: PostgreSQL Cluster, Redis Caching, Elasticsearch<br/>
                Monitoring: Prometheus, Grafana, Distributed Tracing</span>
              </div>
              <div>
                <span className="text-green-400"># LIEFERBARE SPEZIFIKATION</span><br/>
                <span className="text-white">Output: Migrationsstrategie-Dokument einschließlich:<br/>
                - Phasenweise Zerlegungsplan<br/>
                - Risikobewertung mit Mitigationsstrategien<br/>
                - Performance-Benchmarks und Testansatz</span>
              </div>
            </div>
          </div>

          <InfoBox variant="tip">
            <strong>Framework-Vorteile:</strong> Durch strukturierte Prompts erreichen Sie 80-90% Erfolgsrate beim ersten Versuch
          </InfoBox>
        </EnhancedContentSlide>
      ),
      transition: 'slide',
    },

    // ============================================
    // SLIDE 7: Prompt-Vergleich
    // ============================================
    {
      id: 7,
      component: () => (
        <EnhancedGridSlide
          title="Professionelle Beispiele"
          subtitle="Vom schlechten zum enterprise-tauglichen Prompt"
          columns={2}
          background="primary"
          items={[
            <div className="space-y-6">
              <FeatureCard
                icon={<IconSolid name="times-circle" size="xl" className="text-red-400" />}
                title="❌ Unprofessioneller Prompt"
                description="Vage, mehrdeutig, ohne Kontext"
                variant="glass"
              />
              <div className="bg-red-900/30 border border-red-600/50 p-4 rounded-lg">
                <code className="text-red-200 text-sm">
                  "Mach meine Website schneller und füge etwas Sicherheit hinzu. Repariere auch Bugs."
                </code>
              </div>
              <ul className="text-red-200 space-y-1 text-sm">
                <li>• Vage Anforderungen</li>
                <li>• Keine messbaren Ziele</li>
                <li>• Fehlender technischer Kontext</li>
                <li>• Unprofessioneller Umfang</li>
              </ul>
            </div>,
            <div className="space-y-6">
              <FeatureCard
                icon={<IconSolid name="check-circle" size="xl" className="text-green-400" />}
                title="✅ Enterprise-Grade Prompt"
                description="Spezifisch, strukturiert, mit Kontext"
                variant="glass"
              />
              <div className="bg-green-900/30 border border-green-600/50 p-4 rounded-lg">
                <code className="text-green-200 text-xs">
                  "React SPA Performance für E-Commerce optimieren:<br/>
                  • Aktuell: 4.2s LCP, 250ms FID<br/>
                  • Ziel: &lt;2.5s LCP, &lt;100ms FID<br/>  
                  • Tech: React 18, Webpack 5, CDN<br/>
                  • Einschränkungen: SEO-Kompatibilität<br/>
                  • Metriken: Core Web Vitals, Lighthouse"
                </code>
              </div>
              <ul className="text-green-200 space-y-1 text-sm">
                <li>• Spezifische Metriken</li>
                <li>• Klarer technischer Kontext</li>
                <li>• Messbare Erfolgskriterien</li>
                <li>• Professionelle Liefergegenstände</li>
              </ul>
            </div>,
          ]}
        />
      ),
      transition: 'slide',
    },

    // ============================================
    // SLIDE 8: Systematisches Framework
    // ============================================
    {
      id: 8,
      component: () => (
        <EnhancedContentSlide
          title="Systematisches Kontext-Framework"
          subtitle="Der 4-Schritt-Prozess für erfolgreiche KI-Interaktion"
          background="light"
        >
          <div className="flex justify-center items-center gap-4 mb-8 flex-wrap">
            <FeatureCard
              icon={<IconSolid name="bullseye" size="xl" className="text-primary-600" />}
              title="1. Geschäftsziel"
              description="Welcher Geschäftswert?"
              variant="elevated"
            />
            <IconSolid name="arrow-right" size="xl" className="text-gray-400 hidden md:block" />
            <FeatureCard
              icon={<IconSolid name="cogs" size="xl" className="text-secondary-600" />}
              title="2. Technischer Kontext"
              description="Stack, Einschränkungen, Architektur"
              variant="elevated"
            />
            <IconSolid name="arrow-right" size="xl" className="text-gray-400 hidden md:block" />
            <FeatureCard
              icon={<IconSolid name="clipboard-list" size="xl" className="text-accent-600" />}
              title="3. Anforderungen"
              description="Funktional & nicht-funktional"
              variant="elevated"
            />
            <IconSolid name="arrow-right" size="xl" className="text-gray-400 hidden md:block" />
            <FeatureCard
              icon={<IconSolid name="chart-line" size="xl" className="text-green-600" />}
              title="4. Erfolgsmetriken"
              description="Messbare Ergebnisse"
              variant="elevated"
            />
          </div>

          <InfoBox variant="tip">
            <h4 className="font-bold mb-3">🎯 Enterprise Best Practices:</h4>
            <ul className="space-y-2 text-sm">
              <li>• Beginnen Sie mit Geschäftswert, nicht mit technischen Features</li>
              <li>• Compliance- und Sicherheitsanforderungen von Anfang an einbeziehen</li>
              <li>• Messbare Erfolgskriterien vor der Implementierung definieren</li>
              <li>• Rollback- und Monitoring-Strategien spezifizieren</li>
              <li>• Annahmen und Abhängigkeiten explizit dokumentieren</li>
            </ul>
          </InfoBox>
        </EnhancedContentSlide>
      ),
      transition: 'slide',
    },

    // ============================================
    // SLIDE 9: Anti-Patterns
    // ============================================
    {
      id: 9,
      component: () => (
        <EnhancedGridSlide
          title="Enterprise Anti-Patterns zu vermeiden"
          subtitle="Häufige Fehler und wie Sie sie umgehen"
          columns={2}
          background="secondary"
          items={[
            <FeatureCard
              icon={<IconSolid name="exclamation-triangle" size="xl" className="text-red-500" />}
              title="Scope Creep Prompts"
              description="Zu umfangreiche Aufgaben"
              items={[
                'Problem: "Erstelle ein komplettes CRM-System mit KI, Blockchain und Microservices"',
                'Lösung: Aufgabe in kleinere, spezifische Schritte unterteilen',
                'Best Practice: MVP-Ansatz verwenden'
              ]}
            />,
            <FeatureCard
              icon={<IconSolid name="ban" size="xl" className="text-red-500" />}
              title="Fehlende Einschränkungen"
              description="Keine klaren Grenzen"
              items={[
                'Problem: Keine Sicherheitsstandards, Compliance-Regeln oder Coding-Richtlinien angegeben',
                'Lösung: Sicherheitsanforderungen, Compliance-Bedarf und Code-Standards vorab definieren',
                'Best Practice: DSGVO, Barrierefreiheit, Coding-Konventionen im Kontext einbeziehen'
              ]}
            />,
            <FeatureCard
              icon={<IconSolid name="question-circle" size="xl" className="text-red-500" />}
              title="Nicht messbare Ziele"
              description="Vage Erfolgskriterien"
              items={[
                'Problem: "Mach es besser, schneller, skalierbarer"',
                'Lösung: Konkrete Metriken und Benchmarks',
                'Best Practice: SMART-Kriterien anwenden'
              ]}
            />,
            <FeatureCard
              icon={<IconSolid name="random" size="xl" className="text-red-500" />}
              title="Kontext-Switching"
              description="Plötzliche Richtungsänderungen"
              items={[
                'Problem: Anforderungsänderungen ohne Überleitung',
                'Lösung: Explizite Kontextwechsel kommunizieren',
                'Best Practice: Incremental refinement'
              ]}
            />,
          ]}
        />
      ),
      transition: 'slide',
    },

    // ============================================
    // SLIDE 10: Tools & Methoden
    // ============================================
    {
      id: 10,
      component: () => (
        <EnhancedGridSlide
          title="Enterprise Tooling & Methoden"
          subtitle="Professionelle Werkzeuge für systematische KI-Nutzung"
          columns={3}
          background="light"
          items={[
            <FeatureCard
              icon={<IconSolid name="user-tie" size="xl" className="text-primary-600" />}
              title="Rollenbasierte Prompts"
              description="Spezifische Expertenperspektiven definieren"
              items={[
                'Senior-Architekt Rolle',
                'Domain-Experte Perspektive',
                'Stakeholder-spezifische Sicht'
              ]}
            />,
            <FeatureCard
              icon={<IconSolid name="book" size="xl" className="text-secondary-600" />}
              title="Template-Bibliotheken"
              description="Wiederverwendbare Enterprise-Patterns"
              items={[
                'Standardisierte Prompt-Templates',
                'Best-Practice Sammlungen',
                'Domain-spezifische Vorlagen'
              ]}
            />,
            <FeatureCard
              icon={<IconSolid name="link" size="xl" className="text-accent-600" />}
              title="Chain-of-Thought"
              description="Strukturiertes schrittweises Denken"
              items={[
                'Komplexe Probleme zerlegen',
                'Nachvollziehbare Argumentation',
                'Validierbare Zwischenschritte'
              ]}
            />,
            <FeatureCard
              icon={<IconSolid name="tasks" size="xl" className="text-green-600" />}
              title="Anforderungsverfolgung"
              description="Nachverfolgbare Anforderungsketten"
              items={[
                'Requirements Traceability',
                'Change Management',
                'Impact Analysis'
              ]}
            />,
            <FeatureCard
              icon={<IconSolid name="check-circle" size="xl" className="text-secondary-600" />}
              title="Output-Validierung"
              description="Systematische Qualitätskontrolle"
              items={[
                'Automated Quality Gates',
                'Peer Review Prozesse',
                'Compliance Checking'
              ]}
            />,
            <FeatureCard
              icon={<IconSolid name="chart-bar" size="xl" className="text-orange-600" />}
              title="Performance-Metriken"
              description="Messbare Erfolgs-KPIs"
              items={[
                'Response Quality Scores',
                'Time-to-Solution Metrics',
                'User Satisfaction Index'
              ]}
            />,
          ]}
        />
      ),
      transition: 'slide',
    },

    // ============================================
    // SLIDE 11: Context Engineering Setup - Integrationsmöglichkeiten
    // ============================================
    {
      id: 11,
      component: () => (
        <EnhancedContentSlide
          title="� Context Engineering Integrationsmöglichkeiten"
          subtitle="Wie würden Sie Context Engineering in Ihrem Team einrichten?"
          background="secondary"
        >
          <div className="space-y-8">
            <InfoBox variant="tip">
              <strong>💡 Team-Aktivität:</strong> Diskutieren Sie diese Fragen gemeinsam im Team, um den besten Integrationsansatz für Ihre Organisation zu finden!
            </InfoBox>

            {/* Question Cards */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <FeatureCard
                icon={<IconSolid name="question-circle" size="2xl" className="text-yellow-500" />}
                title="Frage 1"
                description="Welche spezialisierten Agents brauchen Sie?"
                items={[
                  'Entwickler? (Frontend, Backend, Full-Stack)',
                  'Architekten? Reviewer?',
                  'DevOps? Security?',
                  'Domain-Experten?'
                ]}
                variant="elevated"
              />
              <FeatureCard
                icon={<IconSolid name="question-circle" size="2xl" className="text-blue-500" />}
                title="Frage 2"
                description="Welche Workflows sind für Sie kritisch?"
                items={[
                  'Git-Strategie?',
                  'Testing-Ansatz?',
                  'Code Review-Prozess?',
                  'Deployment-Workflow?'
                ]}
                variant="elevated"
              />
              <FeatureCard
                icon={<IconSolid name="question-circle" size="2xl" className="text-green-500" />}
                title="Frage 3"
                description="Wie organisieren Sie Ihre Aufgaben?"
                items={[
                  'Sprint-Planung?',
                  'Task-Tracking?',
                  'Dokumentation?',
                  'Knowledge-Sharing?'
                ]}
                variant="elevated"
              />
              <FeatureCard
                icon={<IconSolid name="question-circle" size="2xl" className="text-purple-500" />}
                title="Frage 4"
                description="Was sind Ihre größten Herausforderungen?"
                items={[
                  'Team-Kommunikation?',
                  'Code-Qualität?',
                  'Zeit-Management?',
                  'Wissensverlust?'
                ]}
                variant="elevated"
              />
            </div>

            <InfoBox variant="success">
              <strong>💡 Nächster Schritt:</strong> Notieren Sie Ihre Gedanken und diskutieren Sie im Team - auf der nächsten Folie zeige ich Ihnen konkrete Implementierungslösungen!
            </InfoBox>
          </div>
        </EnhancedContentSlide>
      ),
      transition: 'slide',
    },

    // ============================================
    // SLIDE 12: Context Engineering Setup - Implementation Guide
    // ============================================
    {
      id: 12,
      component: function ContextEngineeringSetupSlide() {
        const [selectedPrompt, setSelectedPrompt] = React.useState<{title: string; prompt: string; description: string; tags: string[]} | null>(null)
        
        return (
          <>
            <EnhancedContentSlide
              title="🚀 Context Engineering Setup"
              subtitle="Klicken Sie auf jede Karte für fertige Prompt-Templates"
              background="light"
            >
              <div className="space-y-8">
                {/* Step 1: Specialized Agents */}
                <div>
                  <h3 className="text-2xl font-bold mb-4 flex items-center gap-2">
                    <span className="flex items-center justify-center w-8 h-8 rounded-full bg-primary-600 text-white text-sm">1</span>
                    Spezialisierte Agents einrichten
                  </h3>
                  <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                    <button
                      onClick={() => setSelectedPrompt(contextEngineeringPrompts.agents.architect)}
                      className="group relative"
                    >
                      <div className="absolute top-2 right-2 bg-primary-600 rounded-full p-1 shadow-lg z-10">
                        <IconSolid name="mouse-pointer" className="text-white w-4 h-4" />
                      </div>
                      <FeatureCard
                        icon={<IconSolid name="drafting-compass" size="xl" className="text-primary-600" />}
                        title="Architect"
                        description="System-Design & Architektur"
                        variant="elevated"
                      />
                    </button>
                    <button
                      onClick={() => setSelectedPrompt(contextEngineeringPrompts.agents.frontend)}
                      className="group relative"
                    >
                      <div className="absolute top-2 right-2 bg-secondary-600 rounded-full p-1 shadow-lg z-10">
                        <IconSolid name="mouse-pointer" className="text-white w-4 h-4" />
                      </div>
                      <FeatureCard
                        icon={<IconSolid name="palette" size="xl" className="text-secondary-600" />}
                        title="Frontend Dev"
                        description="React & TypeScript"
                        variant="elevated"
                      />
                    </button>
                    <button
                      onClick={() => setSelectedPrompt(contextEngineeringPrompts.agents.backend)}
                      className="group relative"
                    >
                      <div className="absolute top-2 right-2 bg-accent-600 rounded-full p-1 shadow-lg z-10">
                        <IconSolid name="mouse-pointer" className="text-white w-4 h-4" />
                      </div>
                      <FeatureCard
                        icon={<IconSolid name="server" size="xl" className="text-accent-600" />}
                        title="Backend Dev"
                        description="APIs & Databases"
                        variant="elevated"
                      />
                    </button>
                    <button
                      onClick={() => setSelectedPrompt(contextEngineeringPrompts.agents.qa)}
                      className="group relative"
                    >
                      <div className="absolute top-2 right-2 bg-green-600 rounded-full p-1 shadow-lg z-10">
                        <IconSolid name="mouse-pointer" className="text-white w-4 h-4" />
                      </div>
                      <FeatureCard
                        icon={<IconSolid name="clipboard-check" size="xl" className="text-green-600" />}
                        title="QA & Review"
                        description="Testing & Quality"
                        variant="elevated"
                      />
                    </button>
                  </div>
                </div>

                {/* Step 2: Workflows */}
                <div>
                  <h3 className="text-2xl font-bold mb-4 flex items-center gap-2">
                    <span className="flex items-center justify-center w-8 h-8 rounded-full bg-secondary-600 text-white text-sm">2</span>
                    Professionelle Workflows definieren
                  </h3>
                  <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                    <button
                      onClick={() => setSelectedPrompt(contextEngineeringPrompts.workflows.git)}
                      className="group relative"
                    >
                      <div className="absolute top-2 right-2 bg-orange-600 rounded-full p-1 shadow-lg z-10">
                        <IconSolid name="mouse-pointer" className="text-white w-4 h-4" />
                      </div>
                      <FeatureCard
                        icon={<IconSolid name="code-branch" size="xl" className="text-orange-600" />}
                        title="Git Workflow"
                        description="Branching & Commits"
                        variant="elevated"
                      />
                    </button>
                    <button
                      onClick={() => setSelectedPrompt(contextEngineeringPrompts.workflows.tdd)}
                      className="group relative"
                    >
                      <div className="absolute top-2 right-2 bg-green-600 rounded-full p-1 shadow-lg z-10">
                        <IconSolid name="mouse-pointer" className="text-white w-4 h-4" />
                      </div>
                      <FeatureCard
                        icon={<IconSolid name="vial" size="xl" className="text-green-600" />}
                        title="TDD"
                        description="Test-Driven Development"
                        variant="elevated"
                      />
                    </button>
                    <button
                      onClick={() => setSelectedPrompt(contextEngineeringPrompts.workflows.specDriven)}
                      className="group relative"
                    >
                      <div className="absolute top-2 right-2 bg-blue-600 rounded-full p-1 shadow-lg z-10">
                        <IconSolid name="mouse-pointer" className="text-white w-4 h-4" />
                      </div>
                      <FeatureCard
                        icon={<IconSolid name="file-alt" size="xl" className="text-blue-600" />}
                        title="Spec-Driven"
                        description="Specification First"
                        variant="elevated"
                      />
                    </button>
                    <button
                      onClick={() => setSelectedPrompt(contextEngineeringPrompts.workflows.reviewing)}
                      className="group relative"
                    >
                      <div className="absolute top-2 right-2 bg-purple-600 rounded-full p-1 shadow-lg z-10">
                        <IconSolid name="mouse-pointer" className="text-white w-4 h-4" />
                      </div>
                      <FeatureCard
                        icon={<IconSolid name="search" size="xl" className="text-purple-600" />}
                        title="Code Review"
                        description="Review Best Practices"
                        variant="elevated"
                      />
                    </button>
                  </div>
                </div>

                {/* Step 3: Task Management & Orchestration */}
                <div>
                  <h3 className="text-2xl font-bold mb-4 flex items-center gap-2">
                    <span className="flex items-center justify-center w-8 h-8 rounded-full bg-accent-600 text-white text-sm">3</span>
                    Task Management & Orchestrierung
                  </h3>
                  <div className="grid grid-cols-2 gap-4">
                    <button
                      onClick={() => setSelectedPrompt(contextEngineeringPrompts.taskManagement)}
                      className="group relative"
                    >
                      <div className="absolute top-2 right-2 bg-primary-600 rounded-full p-1 shadow-lg z-10">
                        <IconSolid name="mouse-pointer" className="text-white w-4 h-4" />
                      </div>
                      <FeatureCard
                        icon={<IconSolid name="tasks" size="xl" className="text-primary-600" />}
                        title="Task Management"
                        description="AI-gestütztes Task-Tracking"
                        variant="elevated"
                      />
                    </button>
                    <button
                      onClick={() => setSelectedPrompt(contextEngineeringPrompts.masterAgent)}
                      className="group relative"
                    >
                      <div className="absolute top-2 right-2 bg-secondary-600 rounded-full p-1 shadow-lg z-10">
                        <IconSolid name="mouse-pointer" className="text-white w-4 h-4" />
                      </div>
                      <FeatureCard
                        icon={<IconSolid name="sitemap" size="xl" className="text-secondary-600" />}
                        title="Master Orchestrator"
                        description="Agent-Koordination"
                        variant="elevated"
                      />
                    </button>
                  </div>
                </div>

                <div className="flex justify-center my-8">
                  <a
                    href="/context-engineering-setup.zip"
                    download="context-engineering-setup.zip"
                    className="inline-flex items-center gap-3 px-8 py-4 bg-gradient-to-r from-primary-600 to-secondary-600 text-white font-bold text-lg rounded-xl shadow-lg hover:shadow-xl hover:scale-105 transition-all duration-300"
                  >
                    <IconSolid name="download" size="xl" />
                    Alle Templates herunterladen (ZIP)
                  </a>
                </div>

                <InfoBox variant="success">
                  <strong>✨ Sofort einsatzbereit:</strong> Alle Templates sind fertig konfiguriert und können direkt in Ihr GitHub Repository kopiert werden!
                </InfoBox>
              </div>
            </EnhancedContentSlide>

            {/* Prompt Modal */}
            {selectedPrompt && (
              <PromptModal
                isOpen={true}
                onClose={() => setSelectedPrompt(null)}
                title={selectedPrompt.title}
                description={selectedPrompt.description}
                prompt={selectedPrompt.prompt}
                tags={selectedPrompt.tags}
              />
            )}
          </>
        )
      },
      transition: 'slide',
    },

    // ============================================
    // SLIDE 13: Live Coding - Lass uns etwas bauen!
    // ============================================
    {
      id: 13,
      component: () => (
        <EnhancedContentSlide
          title="💻 Live Coding Zeit!"
          subtitle="Lass uns eine echte Anwendung mit unserem Context Engineering Setup bauen"
          background="secondary"
        >
          <div className="space-y-8">
            <InfoBox variant="tip">
              <strong>🤔 Denken Sie mit:</strong> Welche kleine, praxisnahe Anwendung sollen wir gemeinsam entwickeln?
            </InfoBox>

            <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
              <FeatureCard
                icon={<IconSolid name="lightbulb" size="2xl" className="text-yellow-500" />}
                title="Gute Beispiel-Eigenschaften"
                description="Was macht eine großartige Live-Coding-Demo aus?"
                items={[
                  '✅ Realistischer Business-Wert',
                  '✅ 15-20 Minuten Entwicklungszeit',
                  '✅ Nutzt mehrere Agents',
                  '✅ Zeigt echte Workflows',
                  '✅ Demonstriert Qualität'
                ]}
                variant="elevated"
              />

              <FeatureCard
                icon={<IconSolid name="code" size="2xl" className="text-blue-500" />}
                title="Technischer Umfang Ideen"
                description="Perfekter Umfang für Live-Demo"
                items={[
                  '🎯 Einfache CRUD-App',
                  '🎯 API mit 2-3 Endpoints',
                  '🎯 Basis UI (3-5 Komponenten)',
                  '🎯 Datenbank-Schema',
                  '🎯 Authentifizierungs-Flow'
                ]}
                variant="elevated"
              />

              <FeatureCard
                icon={<IconSolid name="rocket" size="2xl" className="text-green-500" />}
                title="Beispiel-App Ideen"
                description="Praxisnahe Mini-Anwendungen"
                items={[
                  '📝 Aufgaben/Todo-Manager',
                  '📚 Bibliotheksverwaltung',
                  '🛒 Produktkatalog',
                  '👥 Kontaktverwaltung',
                  '📊 Ausgaben-Tracker'
                ]}
                variant="elevated"
              />
            </div>

            <div className="bg-gradient-to-r from-primary-500/20 to-secondary-500/20 border-2 border-primary-500/50 rounded-xl p-6">
              <h3 className="text-2xl font-bold mb-4 flex items-center gap-3">
                <IconSolid name="bullseye" size="xl" className="text-primary-600" />
                Perfekte Demo-Anforderungen
              </h3>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4 text-lg">
                <div className="flex items-start gap-3">
                  <IconSolid name="layer-group" size="lg" className="text-secondary-600 mt-1" />
                  <div>
                    <strong>Full Stack:</strong> Frontend (React) + Backend (Node/Express) + Datenbank (PostgreSQL/SQLite)
                  </div>
                </div>
                <div className="flex items-start gap-3">
                  <IconSolid name="users" size="lg" className="text-secondary-600 mt-1" />
                  <div>
                    <strong>Mehrere Agents:</strong> Architekt, Frontend Dev, Backend Dev, QA arbeiten zusammen
                  </div>
                </div>
                <div className="flex items-start gap-3">
                  <IconSolid name="tasks" size="lg" className="text-secondary-600 mt-1" />
                  <div>
                    <strong>Echte Workflows:</strong> Git-Strategie, TDD, Code-Reviews, Deployment
                  </div>
                </div>
                <div className="flex items-start gap-3">
                  <IconSolid name="shield-check" size="lg" className="text-secondary-600 mt-1" />
                  <div>
                    <strong>Enterprise-Qualität:</strong> Type-Safety, Error-Handling, Testing, Dokumentation
                  </div>
                </div>
              </div>
            </div>

            <div className="text-center">
              <InfoBox variant="success">
                <strong>💡 Publikums-Input:</strong> Rufen Sie Ihre Ideen rein! Was möchten Sie gebaut sehen?
              </InfoBox>
            </div>
          </div>
        </EnhancedContentSlide>
      ),
      transition: 'slide',
    },

    // ============================================
    // SLIDE 14: Was kommt als Nächstes?
    // ============================================
    {
      id: 14,
      component: () => (
        <EnhancedContentSlide
          title="Was kommt als Nächstes? 🚀"
          subtitle="Context Engineering weiterführen"
          background="primary"
        >
          <div className="space-y-8">
            <div className="text-center mb-8">
              <div className="text-2xl font-light text-white/90 mb-6">
                Sie haben jetzt die <span className="bg-gradient-to-r from-blue-400 to-cyan-400 bg-clip-text text-transparent font-semibold">Grundlage</span> für professionelle KI-Entwicklung
              </div>
            </div>

            <div className="flex justify-center items-center gap-6 mb-8 flex-wrap">
              <FeatureCard
                icon={<IconSolid name="brain" size="xl" className="text-white" />}
                title="Context Engineering"
                description="Professionelle Grundlage ✅"
                variant="glass"
              />
              <IconSolid name="arrow-right" size="xl" className="text-white/60 hidden md:block" />
              <FeatureCard
                icon={<IconSolid name="robot" size="xl" className="text-white" />}
                title="Spezialisierte Agents"
                description="Einsatzbereit ✅"
                variant="glass"
              />
              <IconSolid name="arrow-right" size="xl" className="text-white/60 hidden md:block" />
              <FeatureCard
                icon={<IconSolid name="rocket" size="xl" className="text-white" />}
                title="Live Coding"
                description="Los geht's! 🎯"
                variant="glass"
              />
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-6 max-w-6xl mx-auto">
              <div className="bg-white/10 backdrop-blur-sm border-2 border-white/30 rounded-2xl p-8">
                <h3 className="text-2xl font-bold text-white mb-4 flex items-center gap-3">
                  <IconSolid name="lightbulb" size="xl" className="text-yellow-400" />
                  Zukünftige Session: BMAD Method
                </h3>
                <p className="text-xl text-white/90 mb-4">
                  In einer möglichen zukünftigen Session könnten Sie die <strong>BMAD Method</strong> in Aktion sehen:
                </p>
                <ul className="text-lg text-white/80 space-y-2 ml-6">
                  <li>• Fortgeschrittene Multi-Agent Workflows</li>
                  <li>• Komplette Projektimplementierungen</li>
                  <li>• Enterprise-Grade Automatisierung</li>
                  <li>• Von Anforderung bis Deployment in kürzester Zeit</li>
                </ul>
              </div>

              <div className="bg-white/10 backdrop-blur-sm border-2 border-white/30 rounded-2xl p-8">
                <h3 className="text-2xl font-bold text-white mb-4 flex items-center gap-3">
                  <IconSolid name="plug" size="xl" className="text-cyan-400" />
                  MCP Servers
                </h3>
                <p className="text-xl text-white/90 mb-4">
                  <strong>Model Context Protocol</strong> – KI mit externen Tools verbinden:
                </p>
                <ul className="text-lg text-white/80 space-y-2 ml-6">
                  <li>• KI an externe Tools anbinden</li>
                  <li>• Custom Context Provider</li>
                  <li>• Echtzeit-Informationszugriff</li>
                  <li>• KI-Fähigkeiten erweitern</li>
                </ul>
              </div>
            </div>

            <InfoBox variant="success">
              <strong>Für jetzt:</strong> Lassen Sie uns Context Engineering mit einer Live-Coding-Session in die Praxis umsetzen!
            </InfoBox>
          </div>
        </EnhancedContentSlide>
      ),
      transition: 'slide',
    },

    // ============================================
    // SLIDE 15: Vielen Dank
    // ============================================
    {
      id: 15,
      component: () => (
        <EnhancedContentSlide
          title="Vielen Dank! 🙏"
          subtitle="Fragen? Lassen Sie uns diskutieren!"
          background="primary"
        >
          <div className="space-y-8">
            <div className="text-center space-y-6">
              <div className="flex justify-center gap-8 mb-8">
                <FeatureCard
                  icon={<IconSolid name="envelope" size="2xl" className="text-white" />}
                  title="Kontakt aufnehmen"
                  description="Lassen Sie uns vernetzen und zusammenarbeiten"
                  variant="glass"
                />
                <FeatureCard
                  icon={<IconSolid name="code-branch" size="2xl" className="text-white" />}
                  title="Folgen Sie der Reise"
                  description="Bleiben Sie auf GitHub auf dem Laufenden"
                  variant="glass"
                />
                <FeatureCard
                  icon={<IconSolid name="comments" size="2xl" className="text-white" />}
                  title="Teilen Sie Ihre Erfahrung"
                  description="Lassen Sie uns gemeinsam lernen"
                  variant="glass"
                />
              </div>

              <div className="bg-white/10 backdrop-blur-sm border-2 border-white/30 rounded-2xl p-8 mx-auto max-w-4xl">
                <div className="mb-6">
                  <IconSolid name="quote-left" size="2xl" className="text-white/60 mb-4" />
                </div>
                <blockquote className="text-2xl md:text-3xl font-light text-white/95 italic leading-relaxed mb-6">
                  "Die Zukunft der Programmierung besteht nicht darin, mehr Code zu schreiben, sondern besseren Kontext zu schaffen. KI ersetzt Entwickler nicht—sie verstärkt diejenigen, die die Kunst der Kommunikation beherrschen."
                </blockquote>
                <div className="flex items-center justify-center gap-3 text-white/80">
                  <IconSolid name="user-circle" size="lg" />
                  <cite className="text-lg not-italic">
                    — Andrej Karpathy, KI-Forscher & ehem. Direktor für KI bei Tesla
                  </cite>
                </div>
              </div>

              <div className="mt-12 pt-8 border-t border-white/20">
                <h3 className="text-3xl font-bold text-white mb-4">
                  Bereit, Ihren Entwicklungs-Workflow zu transformieren?
                </h3>
                <p className="text-xl text-white/90 mb-6">
                  Starten Sie Ihre Context Engineering Reise heute!
                </p>
                <div className="flex justify-center gap-6 flex-wrap">
                  <div className="bg-white/20 backdrop-blur-sm rounded-lg px-6 py-3 flex items-center gap-3">
                    <IconSolid name="rocket" size="lg" className="text-green-400" />
                    <span className="text-white font-semibold">Klein anfangen</span>
                  </div>
                  <div className="bg-white/20 backdrop-blur-sm rounded-lg px-6 py-3 flex items-center gap-3">
                    <IconSolid name="sync-alt" size="lg" className="text-blue-400" />
                    <span className="text-white font-semibold">Oft iterieren</span>
                  </div>
                  <div className="bg-white/20 backdrop-blur-sm rounded-lg px-6 py-3 flex items-center gap-3">
                    <IconSolid name="chart-line" size="lg" className="text-yellow-400" />
                    <span className="text-white font-semibold">Ergebnisse messen</span>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </EnhancedContentSlide>
      ),
      transition: 'fade',
    },
  ],
}